/// <reference path="AJAXCommunications_JS.js" />

/**
 * Validation field result.
 * @typedef {Object} VAL_METHOD_RESPONSE
 * @property {boolean} IsValid True to flag that the process pass the test.
 * @property {String} ErrorMessage Error Message.
 * @property {HTMLInputElement} Field input html field
 */

/**
 * Validation Form result.
 * @typedef {Object} VALIDATION_RESPONSE
 * @property {boolean} IsValid True to flag that the process pass the test.
 * @property {Map} Errors All Error Messages (key = HTMLInputElement, value = ErrorMessage).
 */

/**
 * Custom Regular Expresion classes.
 */
class RegExp_JS {
    /**
     * Email rule.
     * @type {RegExp}
     * @public
     */
    static get EMAIL() {
        return /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    }
    /**
     * Name rule.
     * @type {RegExp}
     * @public
     */
    static get NAME() {
        return /^([a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð'.-]+\s*)+$/;
    }
    /**
     * Password rule.
     * @type {RegExp}
     * @public
     */
    static get PASSWORD() {
        return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
    }
}
/**
 * Helper class to test html input fields.
 * @class
 */
class ValidationMethods_JS {
    /**
     * Required field. It works for input with the following attributes: data-val-required.
     * @param {HTMLInputElement} pHTMLInput input field
     * @returns {Promise<VAL_METHOD_RESPONSE>} validation field result.
     * @example
     *  <input name="input" data-val-required="Error message"/>
     */
    static async requiredFieldEval(pHTMLInput) {
        let strErrorMessage = '',
            isValid = true;

        if (typeof pHTMLInput.dataset.valRequired !== 'undefined') {
            strErrorMessage = pHTMLInput.dataset.valRequired;

            switch (pHTMLInput.type.toLowerCase()) {
                case 'checkbox':
                    !pHTMLInput.checked && (isValid = false);
                    break;
                case 'radio':
                    if (!pHTMLInput.name) {
                        isValid = false;
                        strErrorMessage = 'Missing name attribute on radio input.';
                    } else {
                        !document.querySelector(`[name="${pHTMLInput.name}"]:checked`) && (isValid = false);
                    }
                    break;
                case "select-one":
                case "select-multiple":
                    (pHTMLInput.options.length > 0) && (pHTMLInput.options[0].selected) && (isValid = false);
                    break;
                case "file":
                    (pHTMLInput.files.length == 0) && (isValid = false);
                    break;
                default:
                    (!pHTMLInput.value) && (isValid = false);
                    break;
            }
        }
        return Promise.resolve({
            IsValid: isValid,
            ErrorMessage: isValid ? '' : (!!strErrorMessage ? strErrorMessage : 'Required Input Value.')
        });
    }
    // TODO: falta validar este método
    /**
     * Test a regular expression on HTMLInputElement Value.
     * @param {HTMLInputElement} pHTMLInput a Form field
     * @returns {Promise<VAL_METHOD_RESPONSE>} validation field result.
     */
    static async regularExpressionEval(pHTMLInput) {
        // dataset values:
        // data-val-regex-pattern   Regular Expression.
        // data-val-regex   Error message
        let strErrorMessage = '',
            /**
             * @type {VAL_METHOD_RESPONSE}
             */
            objResponse = {
                IsValid: true,
                ErrorMessage: ''
            };
        if (typeof pHTMLInput.dataset.valRegexPattern !== 'undefined') {
            strErrorMessage = pHTMLInput.dataset.valRegex;
            !!pHTMLInput.dataset.valRegexPattern && !(new RegExp(pHTMLInput.dataset.valRegexPattern).test(pHTMLInput.value)) && (objResponse = {
                IsValid: false,
                ErrorMessage: !!strErrorMessage ? strErrorMessage : 'Invalid Input Value.'
            });
        }
        return Promise.resolve(objResponse)
    }
    // TODO: falta validar este método
    /**
     * Test for a "n" max length value.
     * @param {HTMLInputElement} pHTMLInput a Form field
     * @returns {Promise<VAL_METHOD_RESPONSE>} validation field result.
     */
    static async lengthMaxEval(pHTMLInput) {
        // dataset values:
        // data-val-length-max  Max number value.
        // data-val-length-max-err   Error message
        let strValLengthMax = pHTMLInput.dataset.valLengthMax,
            strErrorMessage = pHTMLInput.dataset.valLengthMaxErr,
            /**
             * @type {VAL_METHOD_RESPONSE}
             */
            objResponse = {
                IsValid: true,
                ErrorMessage: ''
            };

        !!strValLengthMax && (pHTMLInput.value.length > parseInt(strValLengthMax)) && (objResponse = {
            IsValid: false,
            ErrorMessage: !!strErrorMessage ? strErrorMessage : 'Invalid Input Value.'
        });

        return Promise.resolve(objResponse)
    }
    // TODO: falta validar este método
    /**
     * Test for a "n" min length value.
     * @param {HTMLInputElement} pHTMLInput a Form field
     * @returns {Promise<VAL_METHOD_RESPONSE>} validation field result.
     */
    static async lengthMinEval(pHTMLInput) {
        // dataset values:
        // data-val-length-min  Min number value.
        // data-val-length-min-err   Error message
        let strValLengthMin = pHTMLInput.dataset.valLengthMin,
            strErrorMessage = pHTMLInput.dataset.valLengthMinErr,
            /**
             * @type {VAL_METHOD_RESPONSE}
             */
            objResponse = {
                IsValid: true,
                ErrorMessage: ''
            };

        !!strValLengthMin && (pHTMLInput.value.length < parseInt(strValLengthMin)) && (objResponse = {
            IsValid: false,
            ErrorMessage: !!strErrorMessage ? strErrorMessage : 'Invalid Input Value.'
        });

        return Promise.resolve(objResponse)
    }
    /**
     * Test for a default class validation @see {@link RegExp_JS}.
     * @param {HTMLInputElement} pHTMLInput Form field.
     * @returns {Promise<VAL_METHOD_RESPONSE>} validation field result.
     */
    static async expressionEval(pHTMLInput) {
        // dataset values:
        // data-val-expression-class  Default class for regular expression.
        // data-val-expression   Error message
        let strErrorMessage = pHTMLInput.dataset.valExpression,
            strProperty = pHTMLInput.dataset.valExpressionClass,

            /**
             * @type {VAL_METHOD_RESPONSE}
             */
            objResponse = {
                IsValid: true,
                ErrorMessage: ''
            };

        (!!strProperty) && (RegExp_JS.hasOwnProperty(strProperty.toLocaleUpperCase())) && !(RegExp_JS[strProperty.toLocaleUpperCase()].test(pHTMLInput.value)) && (objResponse = {
            IsValid: false,
            ErrorMessage: !!strErrorMessage ? strErrorMessage : 'Invalid Input Value.'
        });
        return Promise.resolve(objResponse)
    }
    /**
     * Test for a valid date
     * @param {HTMLInputElement} pHTMLInput Form field.
     * @returns {Promise<VAL_METHOD_RESPONSE>} validation field result.
     */
    static async dateISO8601Eval(pHTMLInput) {
        // dataset values:
        // data-val-date   Error message
        let strErrorMessage = '',
            objDaysByMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31],
            strDateParts = '',
            intYear = 0,
            intMonth = 0,
            intDay = 0,
            isValidDate = true,

            /**
             * @type {VAL_METHOD_RESPONSE}
             */
            objResponse = {
                IsValid: true,
                ErrorMessage: ''
            };

        if (typeof pHTMLInput.dataset.valDate !== 'undefined') {
            strErrorMessage = pHTMLInput.dataset.valDate;

            if (!!pHTMLInput.value) {
                strDateParts = pHTMLInput.value.split('-');
                if (strDateParts.length > 2) {
                    intYear = parseInt(strDateParts[0]);
                    intMonth = parseInt(strDateParts[1]);
                    intDay = parseInt(strDateParts[2]);

                    if (isNaN(intYear) || isNaN(intMonth) || isNaN(intDay)) {
                        isValidDate = false;
                    } else if (intYear < 1 || intYear > 9999) {
                        isValidDate = false;
                    } else if (intMonth < 1 || intMonth > 12) {
                        isValidDate = false;
                    } else if (intMonth == 2 && (intYear % 4 == 0) && ((intYear % 100 != 0) || (intYear % 400 == 0))) { //leap year validation.
                        isValidDate = !(intDay < 1 || intDay > 29);
                    } else if (intDay < 1 || intDay > objDaysByMonth[intMonth - 1]) {
                        isValidDate = false;
                    }
                } else {
                    isValidDate = false;
                }
            } else {
                isValidDate = false;
            }
        }

        objResponse = {
            IsValid: isValidDate,
            ErrorMessage: isValidDate ? '' : (!!strErrorMessage ? strErrorMessage : 'Invalid Input Value.')
        };

        return Promise.resolve(objResponse)
    }
    /**
     * Compare two Inputs for equality.
     * @param {HTMLInputElement} pHTMLInput Form field.
     * @returns {Promise<VAL_METHOD_RESPONSE>} validation field result.
     */
    static async equalToOtherfieldEval(pHTMLInput) {
        // dataset values:
        // data-val-Equalto  Selector for the other field.
        // data-val-Equal    Error message
        let strErrorMessage = '',
            objHtmlInputElement = null,
            /**
             * @type {VAL_METHOD_RESPONSE}
             */
            objResponse = {
                IsValid: true,
                ErrorMessage: ''
            };

        if (pHTMLInput.dataset.valEqualto !== undefined) {
            strErrorMessage = pHTMLInput.dataset.valEqual;
            objHtmlInputElement = document.body.querySelector(pHTMLInput.dataset.valEqualto);
            !!objHtmlInputElement && (objHtmlInputElement.value != pHTMLInput.value) && (objResponse = {
                IsValid: false,
                ErrorMessage: !!strErrorMessage ? strErrorMessage : `Input Value not equal to ${pHTMLInput.dataset.valEqualto}`
            });
        }
        return Promise.resolve(objResponse)
    }
    // TODO: falta validar este método
    /**
     * Test for type of file.
     * @param {HTMLInputElement} pHTMLInput Form field.
     * @returns {Promise<VAL_METHOD_RESPONSE>} validation field result.
     */
    static async typeOfFileEval(pHTMLInput) {
        // dataset values:
        // data-val-type-of-file-mime  Mime Type file.
        // data-val-type-of-file   Error message

        let strErrorMessage = '',
            strValTypeOfFileMime = '',
            isValid = false,
            /**
             * @type {VAL_METHOD_RESPONSE}
             */
            objResponse = {
                IsValid: true,
                ErrorMessage: ''
            };

        if (pHTMLInput.dataset.valTypeOfFileMime !== undefined) {
            strErrorMessage = pHTMLInput.dataset.valTypeOfFile;
            strValTypeOfFileMime = pHTMLInput.dataset.valTypeOfFileMime;

            if (pHTMLInput.type == "file" && pHTMLInput.files.length > 0) {
                isValid = [].slice.call(pHTMLInput.files, 0).every(file => strValTypeOfFileMime.split(',').some(mime => mime.toLocaleUpperCase() == file.type.toLocaleUpperCase()));
                objResponse = {
                    IsValid: isValid,
                    ErrorMessage: isValid ? '' : (!!strErrorMessage ? strErrorMessage : `Invalid Input file(s) format`)
                }
            }
        }
        return Promise.resolve(objResponse)
    }
    // TODO: falta validar este método
    /**
     * Test for size of File.
     * @param {HTMLInputElement} pHTMLInput Form field.
     * @returns {Promise<VAL_METHOD_RESPONSE>} validation field result.
     */
    static async sizeOfFileEval(pHTMLInput) {
        // dataset values:
        // data-val-size-of-file-bytes  Size in bytes.
        // data-val-size-of-file   Error message
        let strErrorMessage = '',
            totalOfBytes = 0,
            /**
             * @type {VAL_METHOD_RESPONSE}
             */
            objResponse = {
                IsValid: true,
                ErrorMessage: ''
            };

        if (typeof pHTMLInput.dataset.valSizeOfFileBytes !== 'undefined') {
            strErrorMessage = pHTMLInput.dataset.valSizeOfFile;
            if (pHTMLInput.type == "file" && pHTMLInput.files.length > 0) {
                //Total of bytes for whole files > valid size bytes
                totalOfBytes = [].slice.call(pHTMLInput.files, 0).reduce((pV, file) => (pV + file.size), 0);

                (totalOfBytes > Number(pHTMLInput.dataset.valSizeOfFileBytes)) && (objResponse = {
                    IsValid: false,
                    ErrorMessage: !!strErrorMessage ? strErrorMessage.replace('{0}', totalOfBytes) : `Invalid Input file(s) size of ${totalOfBytes}.`
                });
            }
        }
        return Promise.resolve(objResponse);
    }
    /**
     * process remote server side validation
     * @param {HTMLInputElement} pHTMLInput  form field
     * @returns {Promise<VAL_METHOD_RESPONSE>} validation field result.
     */
    static async remoteValidation(pHTMLInput) {
        // dataset values:
        // data-val-remote    error message
        // data-val-remote-url    validation endpoint service
        const _errorMessage = pHTMLInput.dataset?.valRemote,
            _remoteUrl = pHTMLInput.dataset?.valRemoteUrl,
            _inputValue = (pHTMLInput.matches('[type="radio"], [type="checkbox"]')) ? pHTMLInput.checked : pHTMLInput.value,
            _inputName = pHTMLInput.dataset?.fieldObject,
            _dto = {},
            _userErrorMessage = 'Se ha presentando un problema. Favor de intentar más tarde.',
            /**
             * @type {VAL_METHOD_RESPONSE}
             */
            _response = {
                IsValid: true,
                ErrorMessage: _errorMessage
            }
            ;
        if (!!_errorMessage && !!_remoteUrl && !!_inputName) {
            _dto[_inputName] = _inputValue;
            await AJAXCommunications_JS.AsyncPostJSON(_remoteUrl, _dto)
                .then(
                    /**
                     * 
                     * @param {AJAX_RESPONSE} resp  server response status 200
                     */
                    resp => {
                        if (resp.isRedirected) {
                            _response.IsValid = false;
                            _response.ErrorMessage = _userErrorMessage;
                        }
                        else if (resp.data.IsSuccessful) {
                            if (!resp.data.Response && resp.data?.Reason.length > 0) {
                                _response.ErrorMessage = resp.data?.Reason?.join(' ');
                            }
                            _response.IsValid = resp.data.Response;
                        }
                        else {
                            _response.IsValid = false;
                            _response.ErrorMessage = _userErrorMessage;
                        }
                    })
                .catch(
                    /**
                     * 
                     * @param {AJAX_ERROR} err server response status 500
                     */
                    err => {
                        _response.IsValid = false;
                        _response.ErrorMessage = _userErrorMessage;
                        console.log(`Error ValidationMethods_JS.remoteValidation`, err);
                    })
                ;
        }
        return Promise.resolve(_response);
    }
}
/**
 * Validation Form Helper.
 * @class
 */
class ValidationForm_JS {
    /**
     * @param {object} pContainer Parent of HTML inputs.
     * @param {Array.<HTMLInputElement|HTMLButtonElement>} pFields form fields.
     * @param {string} pEvents A list of events separated by space that raise input validation.
     * @param {Function} pFnErrorField Callback Function when the field has an error.
     * @param {Function} pFnValidField CallBack Function where the field is valid.
     * @param {Array} pValidationMethods Array of validation functions.
     * @constructor
     */
    constructor(pContainer, pFields, pEvents, pFnErrorField, pFnValidField, pValidationMethods = []) {
        if (!!pContainer && JFM_CONST.CONTAINER_COLLECTION.some(c => c === pContainer.tagName)) {
            /**
             * Form fields.
             * @type {Array.<HTMLInputElement|HTMLButtonElement>}
             */
            this.Fields = pFields;
            if (!this.Fields) throw new Error(`Invalid container fields: zero inputs to validate.`);
            /**
             * Form container
             * @type {HTMLFormElement|HTMLDivElement}
             */
            this.Container = pContainer;
            /**
             * raise a function for each incorrect field.
             * @type {Function}
             */
            this.FnErrorField = (typeof pFnErrorField === 'function') ? pFnErrorField : null;
            /**
             * raise a function for each correct field.
             * @type {Function}
             */
            this.FnValidField = (typeof pFnValidField === 'function') ? pFnValidField : null;
            // pValidationMethods must be a collection of functions
            /**
             * validation methos for each field.
             * @type {Array.<Function<Promise<VAL_METHOD_RESPONSE>>>}
             */
            this.ValidationMethods = null;

            if (Array.isArray(pValidationMethods) && pValidationMethods.length > 0 && pValidationMethods.every(f => typeof f === 'function')) {
                this.ValidationMethods = pValidationMethods;
            }
            /**
             * collection of events to raise separated by spaces.
             * @type {String}
             */
            this.Events = !!pEvents ? pEvents : 'change blur';

            this.init();
        } else {
            throw new Error(`Invalid Container: must be ${JFM_CONST.CONTAINER_COLLECTION.join(' or ')}.`)
        }
    }
    /**
     * Init helper.
     */
    init() {
        this.addEventListenerToFields();
        // If this.ValidationMethods is empty, then it initialized by ValidationMethods_JS
        if (!this.ValidationMethods || this.ValidationMethods.length == 0) {
            this.ValidationMethods = Object.getOwnPropertyNames(ValidationMethods_JS)
                .filter(f => (typeof ValidationMethods_JS[f] === 'function'))
                .map(m => ValidationMethods_JS[m]);
        }
    }
    /**
     * Attach event listeners to a field.
     */
    addEventListenerToFields() {
        this.Events.split(' ').forEach(e => {
            this.Fields.forEach(f => {
                f.addEventListener(e, this.evalFieldAsync.bind(this), false);
            });
        });
    }
    /**
     * Evaluate a field.
     * @param {Object} event event from listener.
     * @returns {Promise<VAL_METHOD_RESPONSE>} Field validation response.
     */
    async evalFieldAsync(event) {
        /*
         * validate individual field.
         */
        let objCurrentField = event.currentTarget, // current field
            objErrorMsgfor = this.Container.querySelector('.invalid-feedback[data-valmsg-for="' + objCurrentField.name + '"]'),
            objValidMsgfor = this.Container.querySelector('.valid-feedback[data-valmsg-for="' + objCurrentField.name + '"]'),
            /**
             * @type {VAL_METHOD_RESPONSE}
             */
            objResponse = null;

        // Loop for every validation method
        objResponse = await this.validateFieldAsync(objCurrentField);

        if (objResponse.IsValid) {
            !!objErrorMsgfor && (objErrorMsgfor.innerText = '');
            if (objCurrentField.matches('[type="radio"]')) {
                this.Container.querySelectorAll(`input[name="${objCurrentField.name}"]`).forEach(f => {
                    f.classList.add('is-valid');
                    f.classList.remove('is-invalid');
                });
            }
            else {
                objCurrentField.classList.add('is-valid');
                objCurrentField.classList.remove('is-invalid');
            }
            !!this.FnValidField && this.FnValidField(objCurrentField, objValidMsgfor, objErrorMsgfor, this.Container);
        } else {
            !!objErrorMsgfor && (objErrorMsgfor.innerText = objResponse.ErrorMessage);
            objCurrentField.classList.remove('is-valid');
            objCurrentField.classList.add('is-invalid');
            !!this.FnErrorField && this.FnErrorField(objCurrentField, objValidMsgfor, objErrorMsgfor, this.Container);
        }
        event.stopPropagation();
        return objResponse;
    }
    /**
     * evaluate the input field against all validation methods 
     * @param {HTMLInputElement} inputField form input field
     * @returns {Promise<VAL_METHOD_RESPONSE>} validation result
     */
    async validateFieldAsync(inputField) {
        /**@type {VAL_METHOD_RESPONSE} */
        let result = null;
        for (let fn of this.ValidationMethods) {
            result = await fn(inputField);
            if (!result.IsValid) {
                return result;
            }
        }
        return result;
    }
    /**
     * Evaluate an entire of collection fields.
     * @returns {Promise<VALIDATION_RESPONSE>} evaluated collection response.
     */
    async evalFormAsync() {
        let objMapErrors = new Map(); // Save all errors
        //check every field in the collection (this.Fields)
        const _validationFieldFunctions = this.Fields.map(async f => {
            let objEvent = {
                currentTarget: f,
                stopPropagation: () => { }
            };
            const _res = await this.evalFieldAsync(objEvent);
            _res.Field = f;
            return _res;
        });

        const _resCollection = await Promise.all(_validationFieldFunctions);

        _resCollection.filter(_res => !_res.IsValid).forEach(_res => objMapErrors.set(_res.Field, _res.ErrorMessage));
        /**
         * @type {VALIDATION_RESPONSE}
         */
        return {
            IsValid: objMapErrors.size == 0,
            Errors: objMapErrors
        }
    }
    /**
     * validate recaptcha input
     * @param {RecaptchaWidget} pWidgetRecaptcha 
     * @param {VALIDATION_RESPONSE} pValidationResponse 
     * @param {string} tokenValue captcha response
     * 
     * @returns {VALIDATION_RESPONSE}
     */
    validateCaptchaToken(pWidgetRecaptcha, pValidationResponse, tokenValue) {
        let _token = tokenValue,
            _hiddenInput = null,
            _labelMessage = null,
            _errorMessage = '',
            _isvalid = false;

        if (_token) {
            _isvalid = true;
        } else {
            // add error to the response
            _isvalid = false;
            pValidationResponse.IsValid = false;
            pValidationResponse.Errors.set(pWidgetRecaptcha.RecaptchaCollection[0].inputForm, pWidgetRecaptcha.ErrorMessage);
        }
        // add error and classes for data representation.
        pWidgetRecaptcha.RecaptchaCollection.forEach(r => {
            _hiddenInput = r.inputForm;
            _labelMessage = this.Container.querySelector(`.invalid-feedback[data-valmsg-for="${_hiddenInput.name}"]`);

            if (_isvalid) {
                _errorMessage = '';
                !!_hiddenInput && (_hiddenInput.classList.add('is-valid'), _hiddenInput.classList.remove('is-invalid'));
            } else {
                _errorMessage = pWidgetRecaptcha.ErrorMessage;
                !!_hiddenInput && (_hiddenInput.classList.add('is-invalid'), _hiddenInput.classList.remove('is-valid'));
            }

            !!_labelMessage && (_labelMessage.innerText = _errorMessage);
        });

        return pValidationResponse;
    }

}